import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useSwot } from '../context/SwotContext';

const { FiX, FiUpload, FiFile, FiAlertTriangle } = FiIcons;

const ImportModal = ({ onClose }) => {
  const { loadData } = useSwot();
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsLoading(true);
    setError('');

    try {
      const text = await file.text();
      const data = JSON.parse(text);

      // Validate the data structure
      if (!data.strengths || !data.weaknesses || !data.opportunities || !data.threats) {
        throw new Error('Invalid SWOT data format');
      }

      loadData(data);
      onClose();
    } catch (err) {
      setError('Invalid JSON file or incorrect format. Please check your file and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Import SWOT Data</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <SafeIcon icon={FiX} className="w-6 h-6" />
          </button>
        </div>

        <div className="space-y-6">
          <div className="text-center">
            <div className="p-8 border-2 border-dashed border-gray-300 rounded-xl hover:border-gray-400 transition-colors">
              <input
                type="file"
                accept=".json"
                onChange={handleFileUpload}
                className="hidden"
                id="file-upload"
                disabled={isLoading}
              />
              <label
                htmlFor="file-upload"
                className="cursor-pointer flex flex-col items-center gap-4"
              >
                <div className="p-4 bg-blue-100 rounded-full">
                  <SafeIcon icon={isLoading ? FiUpload : FiFile} className="w-8 h-8 text-blue-600" />
                </div>
                <div>
                  <p className="text-lg font-semibold text-gray-800">
                    {isLoading ? 'Loading...' : 'Choose JSON file'}
                  </p>
                  <p className="text-sm text-gray-600">
                    Select a previously saved SWOT analysis file
                  </p>
                </div>
              </label>
            </div>
          </div>

          {error && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3"
            >
              <SafeIcon icon={FiAlertTriangle} className="w-5 h-5 text-red-600 flex-shrink-0" />
              <p className="text-red-700 text-sm">{error}</p>
            </motion.div>
          )}

          <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
            <h4 className="font-semibold text-blue-800 mb-2">File Requirements</h4>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• JSON format only</li>
              <li>• Must contain SWOT data structure</li>
              <li>• Previously exported from this tool</li>
            </ul>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ImportModal;